import Footer from "@/components/Footer";
import Navigation from "@/components/Navigation";
import { useEffect } from 'react';
import { Check, Target, Users, Calendar, Clock, Zap, Filter, BarChart } from 'lucide-react';

export default function TaskManagementPage() {
  useEffect(() => {
    const reveals = document.querySelectorAll<HTMLElement>('.reveal');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const el = entry.target as HTMLElement;
        if (entry.isIntersecting) {
          el.classList.add('is-visible');
        } else {
          el.classList.remove('is-visible');
        }
      });
    }, { threshold: 0.15 });

    reveals.forEach(el => observer.observe(el));

    const overlay = document.getElementById('page-transition-overlay');

    const replayHash = (idFrom?: string) => {
      const id = idFrom ?? location.hash?.slice(1);
      if (!id) return;
      const el = document.getElementById(id);
      if (el) {
        el.classList.remove('is-visible');
        // force reflow then re-add so animation plays again
        void (el as HTMLElement).offsetWidth;
        el.classList.add('is-visible');
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    const playTransition = (cb?: () => void) => {
      if (!overlay) {
        cb?.();
        return;
      }
      overlay.classList.remove('hidden');
      // force reflow to restart any internal animation
      void overlay.offsetWidth;
      setTimeout(() => {
        overlay.classList.add('hidden');
        if (cb) setTimeout(cb, 180);
      }, 140);
    };

    const onAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const a = target.closest('a') as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (href.startsWith('#')) {
        e.preventDefault(); // Prevent the default anchor behavior
        const id = href.slice(1);
        playTransition(() => replayHash(id));
      }
    };

    const onHashChange = () => {
      playTransition(() => replayHash());
    };

    window.addEventListener('hashchange', onHashChange);
    document.addEventListener('click', onAnchorClick, true);

    // Remove hash on page load to prevent auto-scroll
    if (window.location.hash) {
      history.pushState("", document.title, window.location.pathname + window.location.search);
    }

    // initial small transition on mount
    setTimeout(() => {
      playTransition(() => replayHash());
    }, 50);

    return () => {
      observer.disconnect();
      window.removeEventListener('hashchange', onHashChange);
      document.removeEventListener('click', onAnchorClick, true);
    };
  }, []);

  return ( 
    <div className="min-h-screen bg-background" style={{ fontFamily: "'Roboto', sans-serif" }}>
      <div id="page-transition-overlay" className="hidden"><div className="bar" /></div>
      <Navigation />
      
      {/* Hero Section - Updated Design with New Image */}
      <section className="relative min-h-[95vh] flex items-center bg-[#050816] text-white overflow-hidden pt-28 pb-8 reveal">
        {/* Decorative Background */}
        <div className="absolute inset-0">
          <div className="absolute -top-32 -left-32 w-[420px] h-[420px] bg-blue-600/20 rounded-full blur-3xl" />
          <div className="absolute top-1/3 -right-24 w-[380px] h-[380px] bg-cyan-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/4 w-[500px] h-[140px] bg-gradient-to-r from-blue-500/10 to-transparent blur-2xl" />
        </div>

        <div className="container relative z-10 mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-14 items-center">

            {/* LEFT CONTENT */}
            <div className="space-y-7 reveal">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-md">
                <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-sm tracking-wide text-gray-200">
                  Task Management Platform
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">
                What is Task Management?
              </h1>

              {/* CONTENT CARD */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-xl shadow-xl space-y-4">
                <p>
                  Task management is more than a plan for the day. It implies following
                  errands from start to finish, appointing subtasks to colleagues, and
                  setting cutoff times to ensure ventures complete on schedule.
                </p>

                <p>
                  Errand the executives programming, similar to Asana, enables groups to
                  work all the more gainfully and effectively. Errand the executives is
                  the way toward dealing with an assignment through its life cycle.
                </p>

                <p>
                  It includes arranging, testing, following, and announcing. Assignment
                  the executives can enable either individual to accomplish objectives, or
                  gatherings of people team up and share information for the achievement
                  of aggregate objectives.
                </p>
              </div>

              <div className="flex flex-wrap gap-4 pt-2">
                <a
                  href="#benefits"
                  className="px-8 py-4 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold shadow-lg hover:-translate-y-1 transition-all"
                >
                  Discover Benefits
                </a>

                <a
                  href="#features"
                  className="px-8 py-4 rounded-xl border border-white/20 bg-white/10 backdrop-blur-md font-semibold hover:bg-white/20 transition-all"
                >
                  Explore Features
                </a>
              </div>
            </div>

            {/* RIGHT IMAGE */}
            <div className="relative reveal">
              <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10" />

                {/* put your image here */}
                <img
                  src="/task.png"
                  alt="Task management dashboard"
                  className="relative w-full h-auto rounded-3xl"
                />
              </div>

              <div className="absolute -bottom-6 -right-6 w-64 h-64 bg-blue-500/30 blur-3xl rounded-full -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section - Light Theme */}
      <section id="benefits" className="py-20 bg-gradient-to-b from-white to-gray-50 reveal">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Key benefits of using task management software
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[{
              icon: "🗂️",
              title: "Keep tasks in one place",
              description: "Spare time, abstain from losing work and data, agent, and track errands to remain on calendar and hit cutoff times."
            }, {
              icon: "⭐",
              title: "Prioritize your work",
              description: "Following undertakings permits everybody to comprehend which are increasingly significant or require additional time, so groups can design their time and fulfill time constraints."
            }, {
              icon: "🤝",
              title: "Improve collaboration",
              description: "Offer records, criticism, thoughts, and all the more so colleagues can take advantage of a similar pool of information at whatever point they need."
            }].map((benefit, index) => (
              <div
                key={index}
                className="reveal group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
              >
                <div className="w-16 h-16 flex items-center justify-center text-3xl mb-6">
                  {benefit.icon}
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {benefit.title}
                </h3>

                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>

                <div className="mt-6 w-12 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Features Section - Dark Theme */}
      <section id="features" className="py-20 bg-gradient-to-b from-gray-900 to-gray-800 text-white reveal">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                  Advanced <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Features</span> Suite
                </h2>
                <p className="text-gray-300 text-lg">
                  Packed with powerful tools designed to streamline your workflow and boost productivity
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  "Advanced Task Filters & Sorting",
                  "Intelligent Priority Management",
                  "Drag & Drop Interface",
                  "Real-time Collaboration",
                  "Customizable Dashboards",
                  "Automated Workflows",
                  "Cross-platform Sync",
                  "Advanced Analytics"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3 group">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                      <Check className="w-5 h-5" />
                    </div>
                    <span className="text-gray-200 group-hover:text-white transition-colors duration-300">{feature}</span>
                  </div>
                ))}
              </div>
              

              <div className="pt-4">
                <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-2xl p-6 border border-white/10">
                  <h4 className="font-bold text-lg mb-2">Key Capabilities</h4>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                      <span>Filter by Type, Priority, Status & Deadline</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                      <span>Unlimited Nested Tasks & Subtasks</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                      <span>Real-time Gantt Charts & Time Tracking</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="relative reveal">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-cyan-500/20">
                <img
                  src="/task-01.png"
                  alt="Advanced task management interface"
                  className="w-full h-auto rounded-3xl border border-white/10"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Basic Features Section - Light Theme */}
      <section id="basic" className="py-20 bg-gradient-to-b from-white to-blue-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative reveal order-2 lg:order-1">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-blue-500/20">
                <img
                  src="/task-01.png"
                  alt="Basic features dashboard"
                  className="w-full h-auto rounded-3xl border border-gray-200"
                />
              </div>
            </div>
            
            <div className="space-y-8 reveal order-1 lg:order-2">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                  Essential <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">Core Features</span>
                </h2>
                <p className="text-gray-600 text-lg">
                  Everything you need for effective task management, built with simplicity and power in mind
                </p>
              </div>
              
              <div className="space-y-6">
                {[ 
                  { title: "Task Templates & Automation", description: "Create reusable templates and automate repetitive tasks" },
                  { title: "Advanced Checklists", description: "Detailed checklists with priority levels and dependencies" },
                  { title: "Gantt Chart Visualization", description: "Visual timeline planning and progress tracking" },
                  { title: "Mobile-First Design", description: "Full functionality on any device, anywhere" },
                  { title: "Smart Workflows", description: "Custom business processes with automated triggers" },
                  { title: "Real-time Reports", description: "Comprehensive analytics and performance insights" }
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-4 group">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center group-hover:from-blue-200 group-hover:to-cyan-200 transition-all duration-300">
                      <Filter className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg group-hover:text-blue-600 transition-colors duration-300">
                        {feature.title}
                      </h4>
                      <p className="text-gray-600 mt-1">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="pt-6">
                <a
                  href="/getintouch"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-300"
                >
                  <Zap className="w-5 h-5" />
                  Get in Touch
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
